/**
 * ============================================================================
 * TASKSOVERVIEW.JS v1.8 - CLEAN UI (View Toggle + Count entfernt)
 * ============================================================================
 *
 * ✅ v1.8: CLEAN UI
 *    - View Toggle entfernt (wird in Tasks.js gesteuert)
 *    - "X Aufgaben" Zähler entfernt
 *    - "⏳ Offen" Sektion-Header entfernt
 *    - Nur "✅ Erledigt" Header bleibt
 *
 * ✅ v1.7: MENTAL LOAD BADGE - EMOJI DETECTION
 * ✅ v1.6: MENTAL LOAD BADGE
 * ✅ v1.5: INTEGER ID SUPPORT
 * ✅ v1.4: BULLETPROOF - tasks = [] Default + Absicherung
 *
 * @version 1.8.0 - Clean UI
 */

import React, { useMemo } from 'react';
import { EmptyState } from '../common/Loading';

console.log('✅ TasksOverview v1.7 loaded - MENTAL LOAD BADGE (EMOJI DETECTION) 🧠');

// ✅ v1.7: Mental Load Emojis aus dem Katalog
const MENTAL_LOAD_EMOJIS = [
  '🧺', '🍳', '🛒', '📋', '🧹', '🗑️', '🧽', '🪣',  // Haushalt
  '📚', '🎒', '🏥', '🎂', '👶', '🧒',              // Kinder
  '💰', '📄', '🏦', '📊', '💳',                    // Finanzen
  '🏠', '🔧', '🌱', '🚗', '❄️', '🔥',              // Haus & Garten
  '👨‍👩‍👧', '📅', '🎁', '✉️', '👵',                   // Familie & Soziales
  '🐕', '🐈', '🦜',                                // Haustiere
  '🧘', '💊', '🏃', '😴',                          // Selbstfürsorge
  '🧠'                                             // Mental Load selbst
];

/**
 * ✅ v1.7: Prüft ob ein Task ein Mental Load Task ist
 * - Kategorie "household" UND
 * - Titel beginnt mit einem bekannten Mental Load Emoji
 */
function isMentalLoadTask(task) {
  // Explizites Flag hat Priorität
  if (task.isMentalLoad === true) return true;

  // Fallback: Erkennung über Emoji + Kategorie
  if (task.category !== 'household') return false;

  const title = task.title || '';
  // Prüfe ob Titel mit einem ML-Emoji beginnt
  return MENTAL_LOAD_EMOJIS.some(emoji => title.startsWith(emoji));
}

const TasksOverview = ({
  tasks = [],              // ✅ BULLETPROOF: Default leeres Array
  onEditTask,
  onToggleTask,
  searchQuery = '',
  filterCategory = 'all',
  filterPriority = 'all',
  filterStatus = 'all',
  viewMode = 'grid',
  // setViewMode entfernt - View Toggle wurde aus UI entfernt
  memberOptions = [],
  getMemberName: getMemberNameProp,
  t,
  isDark = false
}) => {
  
  // ✅ EXTRA ABSICHERUNG: Falls tasks trotzdem undefined ist
  const safeTasks = Array.isArray(tasks) ? tasks : [];
  const safeMembers = Array.isArray(memberOptions) ? memberOptions : [];
  
  const styles = useMemo(() => getStyles(isDark), [isDark]);

  // ============================================================================
  // HELPER FUNCTIONS FÜR MITGLIEDER - ✅ v1.5: INTEGER id Support
  // ============================================================================
  const getMemberName = (assignedTo) => {
    if (getMemberNameProp && typeof getMemberNameProp === 'function') {
      const name = getMemberNameProp(assignedTo);
      if (name && name !== assignedTo && name !== `Mitglied #${assignedTo}`) return name;
    }
    
    if (!assignedTo || assignedTo === 'alle' || assignedTo === 'all') return null;
    
    // ✅ v1.5: Suche nach INTEGER id ODER TEXT user_id
    const member = safeMembers.find(m => 
      m.id === assignedTo || 
      m.id === parseInt(assignedTo) ||
      m.value === assignedTo ||
      m.value === parseInt(assignedTo) ||
      m.user_id === assignedTo
    );
    
    if (member) {
      return member.name || member.email || 'Mitglied';
    }
    
    // Fallback
    if (typeof assignedTo === 'number' || !isNaN(parseInt(assignedTo))) {
      return `Mitglied #${assignedTo}`;
    }
    return assignedTo;
  };

  const getMemberAvatar = (assignedTo) => {
    if (!assignedTo || assignedTo === 'alle' || assignedTo === 'all') return null;
    
    // ✅ v1.5: Suche nach INTEGER id ODER TEXT user_id
    const member = safeMembers.find(m => 
      m.id === assignedTo || 
      m.id === parseInt(assignedTo) ||
      m.value === assignedTo ||
      m.value === parseInt(assignedTo) ||
      m.user_id === assignedTo
    );
    
    return member?.avatar || '👤';
  };

  // ============================================================================
  // FILTERED TASKS - MIT safeTasks
  // ============================================================================
  const filteredTasks = useMemo(() => {
    return safeTasks.filter(task => {
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const title = (task.title || '').toLowerCase();
        const description = (task.description || '').toLowerCase();
        if (!title.includes(query) && !description.includes(query)) {
          return false;
        }
      }
      
      if (filterCategory !== 'all' && task.category !== filterCategory) {
        return false;
      }
      
      if (filterPriority !== 'all' && task.priority !== filterPriority) {
        return false;
      }
      
      if (filterStatus === 'open' && task.done) {
        return false;
      }
      if (filterStatus === 'done' && !task.done) {
        return false;
      }
      
      return true;
    });
  }, [safeTasks, searchQuery, filterCategory, filterPriority, filterStatus]);

  // ============================================================================
  // GROUP BY STATUS
  // ============================================================================
  const groupedTasks = useMemo(() => {
    const open = filteredTasks.filter(t => !t.done);
    const done = filteredTasks.filter(t => t.done);
    return { open, done };
  }, [filteredTasks]);

  // ============================================================================
  // HELPER FUNCTIONS
  // ============================================================================
  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return { bg: '#FEE2E2', text: '#DC2626', icon: '🔴' };
      case 'medium': return { bg: '#FEF3C7', text: '#D97706', icon: '🟡' };
      case 'low': return { bg: '#D1FAE5', text: '#059669', icon: '🟢' };
      default: return { bg: '#F3F4F6', text: '#6B7280', icon: '⚪' };
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'personal': return '🏠';
      case 'work': return '💼';
      case 'household': return '🏡';
      case 'shopping': return '🛒';
      case 'ideas': return '💡';
      default: return '📋';
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    try {
      const date = new Date(dateStr);
      const today = new Date();
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      
      if (date.toDateString() === today.toDateString()) {
        return { text: 'Heute', isOverdue: false, isToday: true };
      }
      if (date.toDateString() === tomorrow.toDateString()) {
        return { text: 'Morgen', isOverdue: false, isToday: false };
      }
      if (date < today) {
        return { 
          text: date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' }),
          isOverdue: true,
          isToday: false 
        };
      }
      return { 
        text: date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' }),
        isOverdue: false,
        isToday: false 
      };
    } catch {
      return null;
    }
  };

  // ============================================================================
  // RENDER: Empty State
  // ============================================================================
  if (filteredTasks.length === 0) {
    return (
      <EmptyState
        icon="✅"
        message={searchQuery || filterCategory !== 'all' || filterPriority !== 'all' || filterStatus !== 'all'
          ? 'Keine Aufgaben gefunden'
          : 'Noch keine Aufgaben vorhanden'
        }
        subMessage={searchQuery || filterCategory !== 'all' || filterPriority !== 'all' || filterStatus !== 'all'
          ? 'Versuche andere Filtereinstellungen'
          : 'Erstelle deine erste Aufgabe!'
        }
      />
    );
  }

  // ============================================================================
  // RENDER: Task Card
  // ============================================================================
  const renderTaskCard = (task) => {
    const priority = getPriorityColor(task.priority);
    const categoryIcon = getCategoryIcon(task.category);
    const dueDate = formatDate(task.due_date || task.dueDate);
    
    const assignedTo = task.assigned_to || task.person;
    const memberName = getMemberName(assignedTo);
    const memberAvatar = getMemberAvatar(assignedTo);
    
    return (
      <div
        key={task.id}
        style={{
          ...styles.taskCard,
          opacity: task.done ? 0.6 : 1
        }}
        onClick={() => onEditTask && onEditTask(task)}
      >
        {/* Checkbox */}
        <div
          style={{
            ...styles.checkbox,
            background: task.done ? '#4A90A4' : 'transparent',
            borderColor: task.done ? '#4A90A4' : styles.checkbox.borderColor
          }}
          onClick={(e) => {
            e.stopPropagation();
            onToggleTask && onToggleTask(task);
          }}
        >
          {task.done && <span style={{ color: 'white', fontSize: '0.75rem' }}>✓</span>}
        </div>
        
        {/* Content */}
        <div style={styles.taskContent}>
          <div style={{
            ...styles.taskTitle,
            textDecoration: task.done ? 'line-through' : 'none'
          }}>
            {task.title || 'Unbenannte Aufgabe'}
          </div>
          
          {task.description && (
            <div style={styles.taskDescription}>
              {task.description.length > 60 
                ? task.description.substring(0, 60) + '...' 
                : task.description
              }
            </div>
          )}
          
          <div style={styles.taskMeta}>
            <span style={styles.metaTag}>
              {categoryIcon} {task.category || 'Allgemein'}
            </span>
            
            <span style={{
              ...styles.metaTag,
              background: priority.bg,
              color: priority.text
            }}>
              {priority.icon} {task.priority || 'Normal'}
            </span>
            
            {dueDate && (
              <span style={{
                ...styles.metaTag,
                background: dueDate.isOverdue ? '#FEE2E2' : dueDate.isToday ? '#DBEAFE' : styles.metaTag.background,
                color: dueDate.isOverdue ? '#DC2626' : dueDate.isToday ? '#2563EB' : styles.metaTag.color
              }}>
                📅 {dueDate.text}
              </span>
            )}
            
            {memberName && memberName !== 'alle' && memberName !== 'Alle' && (
              <span style={{
                ...styles.metaTag,
                background: '#E0E7FF',
                color: '#4F46E5'
              }}>
                {memberAvatar} {memberName}
              </span>
            )}

            {/* ✅ v1.7: Mental Load Badge - Emoji Detection */}
            {isMentalLoadTask(task) && (
              <span style={{
                ...styles.metaTag,
                background: 'linear-gradient(135deg, #EC4899 0%, #F472B6 100%)',
                color: 'white',
                fontWeight: 600
              }}>
                🧠 Mental Load
              </span>
            )}
          </div>
        </div>

        <div style={styles.arrow}>→</div>
      </div>
    );
  };

  // ============================================================================
  // RENDER: Main
  // ============================================================================
  return (
    <div style={styles.container}>

      {/* Open Tasks - ohne Header */}
      {groupedTasks.open.length > 0 && (
        <div style={styles.section}>
          <div style={viewMode === 'grid' ? styles.grid : styles.list}>
            {groupedTasks.open.map(renderTaskCard)}
          </div>
        </div>
      )}

      {/* Completed Tasks - mit kleinem Header */}
      {groupedTasks.done.length > 0 && (
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>
            ✅ Erledigt ({groupedTasks.done.length})
          </h3>
          <div style={viewMode === 'grid' ? styles.grid : styles.list}>
            {groupedTasks.done.map(renderTaskCard)}
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// ✅ PLANIVIO TEAL STYLES
// ============================================================================
function getStyles(isDark) {
  const colors = isDark ? {
    bg: '#111827',
    bgCard: '#1F2937',
    bgSecondary: '#374151',
    text: '#F9FAFB',
    textSecondary: '#D1D5DB',
    textMuted: '#9CA3AF',
    border: '#4B5563',
    borderLight: '#374151',
    accent: '#4A90A4'
  } : {
    bg: '#E8F4F8',
    bgCard: '#FFFFFF',
    bgSecondary: '#F0F9FF',
    text: '#1F2937',
    textSecondary: '#374151',
    textMuted: '#6B7280',
    border: '#B8D4DC',
    borderLight: '#D4E8EC',
    accent: '#4A90A4'
  };

  return {
    container: {
      width: '100%'
    },
    section: {
      marginBottom: '2rem'
    },
    sectionTitle: {
      fontSize: '1rem',
      fontWeight: 600,
      color: colors.text,
      marginBottom: '1rem',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem'
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
      gap: '1rem'
    },
    list: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.5rem'
    },
    taskCard: {
      background: colors.bgCard,
      borderWidth: '2px',
      borderStyle: 'solid',
      borderColor: colors.border,
      borderRadius: '12px',
      padding: '1rem',
      display: 'flex',
      alignItems: 'flex-start',
      gap: '0.75rem',
      cursor: 'pointer',
      transition: 'all 0.2s'
    },
    checkbox: {
      width: '24px',
      height: '24px',
      borderRadius: '6px',
      borderWidth: '2px',
      borderStyle: 'solid',
      borderColor: colors.border,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      cursor: 'pointer',
      transition: 'all 0.2s'
    },
    taskContent: {
      flex: 1,
      minWidth: 0
    },
    taskTitle: {
      fontSize: '1rem',
      fontWeight: 600,
      color: colors.text,
      marginBottom: '0.25rem',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap'
    },
    taskDescription: {
      fontSize: '0.85rem',
      color: colors.textMuted,
      marginBottom: '0.5rem',
      lineHeight: 1.4
    },
    taskMeta: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '0.5rem'
    },
    metaTag: {
      fontSize: '0.75rem',
      padding: '0.2rem 0.5rem',
      borderRadius: '4px',
      background: colors.borderLight,
      color: colors.textSecondary,
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.25rem'
    },
    arrow: {
      color: colors.textMuted,
      fontSize: '1.25rem',
      flexShrink: 0
    }
  };
}

export default TasksOverview;